'use client';

import {
  Badge,
  Button,
  DatePicker,
  Input,
  Label,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from '@commenda/kakashi';
import type { FormComponent, FormModule, FormStep } from '@commenda/types';
import { DEFAULT_MAX_FILE_SIZE_MB } from './dsl-constants';
import { isModuleLockingComponent } from './types';
import { X } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import {
  AutoDetectFieldEditor,
  BankAccountEditor,
  BindingsEditor,
  BusinessEntitySelectOrCreateEditor,
  DataSourceEditor,
  DirectorDocumentEditor,
  DocumentEditor,
  EntitySelectEditor,
  EntitySelectOrCreateEditor,
  FormSettingsEditor,
  FormSummaryEditor,
  GovernmentIdentifierEditor,
  KeyPersonsEditor,
  LocationEditor,
  ModuleEditor,
  NestedFormEditor,
  OptionsEditor,
  OwnershipEditor,
  StepEditor,
  ValidationEditor,
  VisibilityEditor,
} from './components/editors';
import { FormBuilderHook } from './hooks/useFormBuilder';
import { getAllFieldIds } from './utils/dsl-traversal';

// Dynamic import for MDEditor to avoid ES Module issues with rehype-prism-plus
const MDEditor = dynamic(() => import('@uiw/react-md-editor'), {
  ssr: false,
  loading: () => (
    <div className="h-32 border rounded-md bg-bg-secondary animate-pulse" />
  ),
});

interface PropertyEditorProps {
  formBuilder: FormBuilderHook;
}

// Component Editor
function ComponentEditor({
  component,
  stepId,
  moduleId,
  formBuilder,
}: {
  component: FormComponent;
  stepId: string;
  moduleId: string;
  formBuilder: FormBuilderHook;
}) {
  const [localComponent, setLocalComponent] = useState(component);
  // Track latest props in a ref so sequential updateProps calls don't overwrite each other
  const latestPropsRef = useRef(localComponent.props);
  latestPropsRef.current = localComponent.props;

  useEffect(() => {
    setLocalComponent(component);
  }, [component]);

  const handleUpdate = (updates: Partial<FormComponent>) => {
    formBuilder.updateComponent(stepId, moduleId, component.id, updates);
    setLocalComponent((prev) => ({ ...prev, ...updates }));
  };

  const updateProps = (key: string, value: unknown) => {
    const newProps = { ...latestPropsRef.current, [key]: value };
    latestPropsRef.current = newProps;
    handleUpdate({ props: newProps });
  };

  return (
    <div className="h-full flex flex-col bg-bg-primary border-l border-border-secondary overflow-hidden">
      <div className="p-2.5 px-4 border-b border-border-secondary flex items-center justify-between">
        <div>
          <h2 className="txt-medium-medium text-text-primary">
            {component.component}
          </h2>
        </div>
        <Button
          type="button"
          variant="tertiary"
          size="sm"
          onClick={() => formBuilder.clearSelection()}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Basic Properties */}
        <div className="space-y-4">
          {localComponent.component !== 'Alert' && (
            <>
              <div className="space-y-2">
                <Label>Label</Label>
                <Input
                  value={localComponent.label || ''}
                  onChange={(e) =>
                    setLocalComponent((prev) => ({
                      ...prev,
                      label: e.target.value,
                    }))
                  }
                  onBlur={() => handleUpdate({ label: localComponent.label })}
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Field ID
                  <Badge variant="blue" size="sm">
                    New
                  </Badge>
                </Label>
                <Input
                  value={localComponent.fieldId || ''}
                  onChange={(e) => {
                    const value = e.target.value
                      .replace(/[^a-zA-Z0-9_]/g, '')
                      .replace(/^[0-9]/, '');
                    setLocalComponent((prev) => ({
                      ...prev,
                      fieldId: value,
                    }));
                  }}
                  onBlur={() =>
                    handleUpdate({ fieldId: localComponent.fieldId })
                  }
                  placeholder="e.g., country, jurisdictionId"
                />
                <p className="txt-xs-regular text-text-tertiary">
                  Unique key for this field's data. Used to reference this
                  field's value in other forms via workflow context.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={localComponent.description || ''}
                  onChange={(e) =>
                    setLocalComponent((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  onBlur={() =>
                    handleUpdate({
                      description: localComponent.description || undefined,
                    })
                  }
                  placeholder="Enter description..."
                  rows={2}
                />
                <p className="txt-xs-regular text-text-tertiary">
                  Additional text displayed below the label
                </p>
              </div>

              <div className="space-y-2">
                <Label>Placeholder</Label>
                <Input
                  value={localComponent.placeholder || ''}
                  onChange={(e) =>
                    setLocalComponent((prev) => ({
                      ...prev,
                      placeholder: e.target.value,
                    }))
                  }
                  onBlur={() =>
                    handleUpdate({ placeholder: localComponent.placeholder })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Default Value</Label>
                {localComponent.component === 'DateInput' ? (
                  <DatePicker
                    date={(localComponent.defaultValue as string) || undefined}
                    setDate={(date) => {
                      setLocalComponent((prev) => ({
                        ...prev,
                        defaultValue: date,
                      }));
                      handleUpdate({ defaultValue: date });
                    }}
                  />
                ) : (
                  <Input
                    value={String(localComponent.defaultValue || '')}
                    onChange={(e) =>
                      setLocalComponent((prev) => ({
                        ...prev,
                        defaultValue: e.target.value || undefined,
                      }))
                    }
                    onBlur={() =>
                      handleUpdate({
                        defaultValue: localComponent.defaultValue || undefined,
                      })
                    }
                    placeholder="Enter default value..."
                  />
                )}
                <p className="txt-xs-regular text-text-tertiary">
                  Pre-filled value when form loads
                </p>
              </div>

              <div className="space-y-2">
                <Label>Default expression (optional)</Label>
                <Input
                  value={localComponent.defaultExpression || ''}
                  onChange={(e) =>
                    setLocalComponent((prev) => ({
                      ...prev,
                      defaultExpression: e.target.value || undefined,
                    }))
                  }
                  onBlur={() =>
                    handleUpdate({
                      defaultExpression:
                        localComponent.defaultExpression || undefined,
                    })
                  }
                  placeholder="$today, $now, $field:fieldId, $context:key"
                />
                <p className="txt-xs-regular text-text-tertiary">
                  Tokens: $today (date), $now (ISO), $field:fieldId (copy from
                  field), $context:key (from context)
                </p>
              </div>

              {/* NumberInput-specific */}
              {localComponent.component === 'NumberInput' && (
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Format with commas</Label>
                    <p className="txt-xs-regular text-text-tertiary">
                      Display numbers with comma separators (e.g., 1,000)
                    </p>
                  </div>
                  <Switch
                    checked={
                      (localComponent.props?.formatWithCommas as boolean) !==
                      false
                    }
                    onCheckedChange={(checked) =>
                      updateProps('formatWithCommas', checked)
                    }
                  />
                </div>
              )}
            </>
          )}
          {/* Alert-specific */}
          {localComponent.component === 'Alert' && (
            <>
              <div className="space-y-2">
                <Label>Message</Label>
                <Textarea
                  value={(localComponent.props?.message as string) || ''}
                  onChange={(e) => updateProps('message', e.target.value)}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={(localComponent.props?.type as string) || 'info'}
                  onValueChange={(value) => updateProps('type', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <Label>Dismissible</Label>
                <Switch
                  checked={
                    (localComponent.props?.dismissible as boolean) ?? true
                  }
                  onCheckedChange={(checked) =>
                    updateProps('dismissible', checked)
                  }
                />
              </div>
            </>
          )}
          {/* EntitySelect-specific */}
          {localComponent.component === 'EntitySelect' && (
            <EntitySelectEditor
              props={localComponent.props as Record<string, unknown>}
              onUpdateProps={updateProps}
              onUpdateLabel={(label) => handleUpdate({ label })}
              currentLabel={localComponent.label}
            />
          )}
          {/* FileUpload-specific */}
          {localComponent.component === 'FileUpload' && (
            <>
              <div className="space-y-2">
                <Label>Accepted File Types</Label>
                <MultiSelect
                  options={[
                    { label: 'PDF (.pdf)', value: '.pdf' },
                    { label: 'Word (.doc)', value: '.doc' },
                    { label: 'Word (.docx)', value: '.docx' },
                    { label: 'Excel (.xls)', value: '.xls' },
                    { label: 'Excel (.xlsx)', value: '.xlsx' },
                    { label: 'CSV (.csv)', value: '.csv' },
                    { label: 'JSON (.json)', value: '.json' },
                    { label: 'Text (.txt)', value: '.txt' },
                    { label: 'JPEG (.jpg)', value: '.jpg' },
                    { label: 'PNG (.png)', value: '.png' },
                  ]}
                  defaultValue={
                    Array.isArray(localComponent.props?.acceptedFileTypes)
                      ? (localComponent.props.acceptedFileTypes as string[])
                      : []
                  }
                  onValueChange={(fileTypes) => {
                    updateProps('acceptedFileTypes', fileTypes);
                  }}
                  placeholder="Select file types..."
                  hideSelectAll
                />
              </div>

              <div className="space-y-2">
                <Label>Max File Size (MB)</Label>
                <Input
                  type="number"
                  value={(localComponent.props?.maxSizeInMB as number) || DEFAULT_MAX_FILE_SIZE_MB}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    updateProps(
                      'maxSizeInMB',
                      Number.isNaN(value) ? 10 : value
                    );
                  }}
                  min={1}
                />
                <p className="txt-xs-regular text-text-tertiary">
                  Maximum file size in megabytes
                </p>
              </div>

              <div className="space-y-2">
                <Label>Tooltip</Label>
                <Input
                  value={(localComponent.props?.tooltip as string) || ''}
                  onChange={(e) => updateProps('tooltip', e.target.value)}
                  placeholder="Additional help text"
                />
                <p className="txt-xs-regular text-text-tertiary">
                  Additional help text displayed below the uploader
                </p>
              </div>
            </>
          )}
          {/* MultiFileUpload-specific */}
          {localComponent.component === 'MultiFileUpload' && (
            <>
              <div className="space-y-2">
                <Label>Accepted File Types</Label>
                <MultiSelect
                  options={[
                    { label: 'PDF (.pdf)', value: '.pdf' },
                    { label: 'Word (.doc)', value: '.doc' },
                    { label: 'Word (.docx)', value: '.docx' },
                    { label: 'Excel (.xls)', value: '.xls' },
                    { label: 'Excel (.xlsx)', value: '.xlsx' },
                    { label: 'CSV (.csv)', value: '.csv' },
                    { label: 'JSON (.json)', value: '.json' },
                    { label: 'Text (.txt)', value: '.txt' },
                    { label: 'JPEG (.jpg)', value: '.jpg' },
                    { label: 'PNG (.png)', value: '.png' },
                  ]}
                  defaultValue={
                    Array.isArray(localComponent.props?.acceptedFileTypes)
                      ? (localComponent.props.acceptedFileTypes as string[])
                      : []
                  }
                  onValueChange={(fileTypes) => {
                    updateProps('acceptedFileTypes', fileTypes);
                  }}
                  placeholder="Select file types..."
                  hideSelectAll
                />
              </div>

              <div className="space-y-2">
                <Label>Max File Size (MB)</Label>
                <Input
                  type="number"
                  value={(localComponent.props?.maxSizeInMB as number) || DEFAULT_MAX_FILE_SIZE_MB}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    updateProps(
                      'maxSizeInMB',
                      Number.isNaN(value) ? 10 : value
                    );
                  }}
                  min={1}
                />
                <p className="txt-xs-regular text-text-tertiary">
                  Maximum file size in megabytes
                </p>
              </div>

              <div className="space-y-2">
                <Label>Max Files</Label>
                <Input
                  type="number"
                  value={
                    (localComponent.props?.maxFiles as number) || undefined
                  }
                  onChange={(e) => {
                    const { value } = e.target;
                    if (value === '') {
                      updateProps('maxFiles', undefined);
                    } else {
                      const numValue = parseInt(value, 10);
                      updateProps(
                        'maxFiles',
                        Number.isNaN(numValue) ? undefined : numValue
                      );
                    }
                  }}
                  min={1}
                  placeholder="No limit"
                />
                <p className="txt-xs-regular text-text-tertiary">
                  Maximum number of files allowed (leave empty for no limit)
                </p>
              </div>

              <div className="space-y-2">
                <Label>Custom Message</Label>
                <Textarea
                  value={(localComponent.props?.message as string) || ''}
                  onChange={(e) => updateProps('message', e.target.value)}
                  rows={2}
                  placeholder="Max file size: 10MB. Accepted formats: .pdf, .doc"
                />
                <p className="txt-xs-regular text-text-tertiary">
                  Custom message displayed in the upload area (optional)
                </p>
              </div>

              <div className="space-y-2">
                <Label>Tooltip</Label>
                <Input
                  value={(localComponent.props?.tooltip as string) || ''}
                  onChange={(e) => updateProps('tooltip', e.target.value)}
                  placeholder="Additional help text"
                />
                <p className="txt-xs-regular text-text-tertiary">
                  Additional help text displayed below the uploader
                </p>
              </div>
            </>
          )}
          {/* Button-specific
          {localComponent.component === 'ButtonComponent' && (
            <>
              <div className="space-y-2">
                <Label>Button Label</Label>
                <Input
                  value={(localComponent.props?.label as string) || ''}
                  onChange={(e) => updateProps('label', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Variant</Label>
                <Select
                  value={(localComponent.props?.variant as string) || 'primary'}
                  onValueChange={(value) => updateProps('variant', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">Primary</SelectItem>
                    <SelectItem value="secondary">Secondary</SelectItem>
                    <SelectItem value="tertiary">Tertiary</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )} */}
        </div>

        {/* Data Source - Show FIRST for Select/RadioGroup/CheckboxGroup/AsyncSelect */}
        {['Select', 'RadioGroup', 'CheckboxGroup', 'AsyncSelect'].includes(
          localComponent.component
        ) && (
          <DataSourceEditor
            dataSource={localComponent.dataSource}
            onChange={(dataSource) => handleUpdate({ dataSource })}
            currentFieldId={localComponent.fieldId}
            dsl={formBuilder.dsl}
            defaultExpanded
          />
        )}

        {/* Static Options - Only shown when data source is static or not set */}
        {['Select', 'RadioGroup', 'CheckboxGroup', 'AsyncSelect'].includes(
          localComponent.component
        ) &&
          (!localComponent.dataSource?.type ||
            localComponent.dataSource.type === 'static') && (
            <OptionsEditor
              options={localComponent.options || []}
              onChange={(options) => handleUpdate({ options })}
            />
          )}

        {/* Radio Layout Option for RadioGroup */}
        {localComponent.component === 'RadioGroup' && (
          <div className="space-y-4">
            <h3 className="txt-small-medium text-text-secondary">
              Layout Options
            </h3>
            <div className="space-y-2">
              <Label>Radio Button Layout</Label>
              <Select
                value={
                  (localComponent.props?.radioLayout as string) || 'vertical'
                }
                onValueChange={(value) =>
                  updateProps('radioLayout', value as 'horizontal' | 'vertical')
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vertical">Vertical (Stacked)</SelectItem>
                  <SelectItem value="horizontal">
                    Horizontal (Side by Side)
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="txt-xs-regular text-text-tertiary">
                Choose how radio options are displayed
              </p>
            </div>
          </div>
        )}

        {/* Field Bindings - prefill this field from another field */}
        {localComponent.fieldId && (
          <BindingsEditor
            bindings={localComponent.bindings}
            availableFields={getAllFieldIds(formBuilder.dsl).filter(
              (f) => f.fieldId !== localComponent.fieldId
            )}
            onChange={(bindings) => handleUpdate({ bindings })}
          />
        )}

        {/* FormSummary-specific configuration */}
        {localComponent.component === 'FormSummary' && (
          <FormSummaryEditor
            props={localComponent.props as Record<string, unknown>}
            onUpdateProps={updateProps}
          />
        )}

        {/* NestedForm-specific configuration */}
        {localComponent.component === 'NestedForm' && (
          <NestedFormEditor
            props={localComponent.props as Record<string, unknown>}
            onUpdateProps={updateProps}
          />
        )}

        {/* Data Source - For other components (not Select/RadioGroup/CheckboxGroup/AsyncSelect) */}
        {!['Select', 'RadioGroup', 'CheckboxGroup', 'AsyncSelect'].includes(
          localComponent.component
        ) &&
          localComponent.component !== 'EntitySelect' &&
          localComponent.component !== 'AutoDetectField' &&
          localComponent.component !== 'GovernmentIdentifier' &&
          localComponent.component !== 'BankAccount' &&
          localComponent.component !== 'Location' &&
          localComponent.component !== 'Document' &&
          localComponent.component !== 'DirectorDocument' &&
          localComponent.component !== 'EntitySelectOrCreate' &&
          localComponent.component !== 'NestedForm' &&
          localComponent.component !== 'KeyPersons' &&
          localComponent.component !== 'Ownership' && (
            <DataSourceEditor
              dataSource={localComponent.dataSource}
              onChange={(dataSource) => handleUpdate({ dataSource })}
              currentFieldId={localComponent.fieldId}
              dsl={formBuilder.dsl}
            />
          )}

        {/* AutoDetectField-specific configuration */}
        {localComponent.component === 'AutoDetectField' && (
          <AutoDetectFieldEditor
            dataSource={localComponent.dataSource}
            onChange={(dataSource) => handleUpdate({ dataSource })}
            currentFieldId={localComponent.fieldId}
            dsl={formBuilder.dsl}
            onUpdateLabel={(label) => handleUpdate({ label })}
            onUpdateProps={updateProps}
          />
        )}

        {/* GovernmentIdentifier-specific configuration */}
        {localComponent.component === 'GovernmentIdentifier' && (
          <GovernmentIdentifierEditor
            props={localComponent.props as Record<string, unknown>}
            dataSource={localComponent.dataSource}
            onUpdateProps={updateProps}
            onDataSourceChange={(dataSource) => handleUpdate({ dataSource })}
            currentFieldId={localComponent.fieldId}
            dsl={formBuilder.dsl}
          />
        )}

        {/* BankAccount-specific configuration */}
        {localComponent.component === 'BankAccount' && (
          <BankAccountEditor
            props={localComponent.props as Record<string, unknown>}
            dataSource={localComponent.dataSource}
            onUpdateProps={updateProps}
            onDataSourceChange={(dataSource) => handleUpdate({ dataSource })}
            currentFieldId={localComponent.fieldId}
            dsl={formBuilder.dsl}
          />
        )}

        {/* Location-specific configuration */}
        {localComponent.component === 'Location' && (
          <LocationEditor
            props={localComponent.props as Record<string, unknown>}
            dataSource={localComponent.dataSource}
            onUpdateProps={updateProps}
            onDataSourceChange={(dataSource) => handleUpdate({ dataSource })}
            currentFieldId={localComponent.fieldId}
            dsl={formBuilder.dsl}
          />
        )}

        {/* Document-specific configuration */}
        {localComponent.component === 'Document' && (
          <DocumentEditor
            props={localComponent.props as Record<string, unknown>}
            dataSource={localComponent.dataSource}
            onUpdateProps={updateProps}
            onDataSourceChange={(dataSource) => handleUpdate({ dataSource })}
            currentFieldId={localComponent.fieldId}
            dsl={formBuilder.dsl}
          />
        )}

        {/* DirectorDocument-specific configuration */}
        {localComponent.component === 'DirectorDocument' && (
          <DirectorDocumentEditor
            props={localComponent.props as Record<string, unknown>}
            dataSource={localComponent.dataSource}
            onUpdateProps={updateProps}
            onDataSourceChange={(dataSource) => handleUpdate({ dataSource })}
          />
        )}

        {/* EntitySelectOrCreate-specific configuration */}
        {localComponent.component === 'EntitySelectOrCreate' && (
          <EntitySelectOrCreateEditor
            props={localComponent.props as Record<string, unknown>}
            template={localComponent.template}
            onUpdateProps={updateProps}
            onUpdateTemplate={(template) => handleUpdate({ template })}
            onUpdateLabel={(label) => handleUpdate({ label })}
            onUpdateValidation={(validation) => handleUpdate({ validation })}
            currentLabel={localComponent.label}
            currentValidation={localComponent.validation}
          />
        )}

        {/* KeyPersons-specific configuration */}
        {localComponent.component === 'KeyPersons' && (
          <KeyPersonsEditor
            props={localComponent.props as Record<string, unknown>}
            onUpdateProps={updateProps}
          />
        )}

        {/* Ownership-specific configuration */}
        {localComponent.component === 'Ownership' && (
          <OwnershipEditor
            props={localComponent.props as Record<string, unknown>}
            onUpdateProps={updateProps}
          />
        )}

        {/* BusinessEntitySelectOrCreate-specific configuration */}
        {localComponent.component === 'BusinessEntitySelectOrCreate' && (
          <BusinessEntitySelectOrCreateEditor
            props={localComponent.props as Record<string, unknown>}
            template={localComponent.template}
            onUpdateProps={updateProps}
            onUpdateTemplate={(template) => handleUpdate({ template })}
            onUpdateLabel={(label) => handleUpdate({ label })}
            onUpdateValidation={(validation) => handleUpdate({ validation })}
            currentLabel={localComponent.label}
            currentValidation={localComponent.validation}
          />
        )}

        {/* Visibility Conditions */}
        <VisibilityEditor
          visibility={localComponent.visibility}
          availableFields={getAllFieldIds(formBuilder.dsl).filter(
            (f) => f.fieldId !== localComponent.fieldId
          )}
          onChange={(visibility) => handleUpdate({ visibility })}
        />

        {/* Validation */}
        {localComponent.fieldId && (
          <ValidationEditor
            validation={localComponent.validation}
            componentType={localComponent.component}
            onChange={(validation) => handleUpdate({ validation })}
            availableFields={getAllFieldIds(formBuilder.dsl)}
            currentFieldId={localComponent.fieldId}
            isInRepeatableModule={
              !!formBuilder.dsl.steps
                ?.find((s) => s.id === stepId)
                ?.modules?.find((m) => m.id === moduleId)?.repeatable?.enabled
            }
          />
        )}

        {/* Guidance */}
        <div className="space-y-4">
          <h3 className="txt-small-medium text-text-secondary">Guidance</h3>
          <div className="space-y-2">
            <Label>Help Text (Markdown)</Label>
            <MDEditor
              // style={{ backgroundColor: 'transparent' }}
              data-color-mode="light"
              value={localComponent.guidance?.text || ''}
              onChange={(value) => {
                setLocalComponent((prev) => ({
                  ...prev,
                  guidance: { text: value },
                }));
                handleUpdate({
                  guidance: { text: value },
                });
              }}
              textareaProps={{
                placeholder: 'Enter help text...',
              }}
              className="guidance-markdown"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export function PropertyEditor({ formBuilder }: PropertyEditorProps) {
  const { selection, getSelectedItem } = formBuilder;
  const selectedItem = getSelectedItem();

  if (!selection.type) {
    return <FormSettingsEditor formBuilder={formBuilder} />;
  }

  if (selection.type === 'step' && selectedItem) {
    return (
      <StepEditor step={selectedItem as FormStep} formBuilder={formBuilder} />
    );
  }

  if (selection.type === 'module' && selectedItem) {
    const module = selectedItem as FormModule;
    const isNestedFormLocked =
      module.components.length === 1 &&
      isModuleLockingComponent(module.components[0].component);
    if (isNestedFormLocked && module.components[0]) {
      return (
        <ComponentEditor
          component={module.components[0]}
          stepId={selection.stepId!}
          moduleId={selection.moduleId!}
          formBuilder={formBuilder}
        />
      );
    }
    return (
      <ModuleEditor
        module={module}
        stepId={selection.stepId!}
        formBuilder={formBuilder}
      />
    );
  }

  if (selection.type === 'component' && selectedItem) {
    return (
      <ComponentEditor
        component={selectedItem as FormComponent}
        stepId={selection.stepId!}
        moduleId={selection.moduleId!}
        formBuilder={formBuilder}
      />
    );
  }

  return null;
}

export default PropertyEditor;
